import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  useAuthStore,
  useHasAnyPermission,
  useHasPermission,
} from "@/store/authStore";
import { useOrganizationStore } from "@/store/organizationStore";
import { useAgentWorkflowStore } from "@/store/agentsStore";
import { useStandardAgentDisplayName } from "@/modules/standardAgent/useStandardAgentDisplayName";
import { useAgentTasksStore } from "@/store/agentTasksStore";
import { AgentWorkflowRunTaskStatus, Permission } from "@forgepoint/shared";
import { useFeatureFlag } from "@/store/featureFlagStore";
import { SidebarHeader } from "@/components/ui/sidebar";
import {
  Building2,
  ChevronUp,
  GraduationCap,
  Users,
  Settings as SettingsIcon,
} from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router";
import { motion } from "framer-motion";
import React, { useCallback, useEffect, useRef, useState } from "react";
import logoBlack from "@/assets/logo_black.svg";
import logoWhite from "@/assets/logo_white.svg";
import {
  Bot,
  getAgentIcon,
  getPrimaryItems,
  getRecordsItems,
  getSettingsGroups,
  isActiveLink,
} from "@/components/sidebar-items";
import { OrgSwitcherMenuItems } from "@/components/OrgSwitcherMenuItems";
import { useOrganizationMembershipsStore } from "@/store/organizationMembershipsStore";
import { SidebarMainPanel } from "@/components/sidebar-main-panel";
import { SidebarSettingsPanel } from "@/components/sidebar-settings-panel";
import { useOnboardingStore } from "@/modules/onboarding/onboardingStore";

/**
 * Main application sidebar. Renders a sliding two-panel layout that
 * transitions between the main navigation and settings navigation.
 */
export function AppSidebar() {
  // --- Hooks ---
  const organizationName = useAuthStore(
    (state) => state.user?.organization.name,
  );
  const canViewAnalytics = useHasPermission(Permission.ANALYTICS_VIEW);
  const canEditSettings = useHasPermission(Permission.SETTINGS_EDIT);
  const hasReportSettingsPermission = useHasPermission(
    Permission.SETTINGS_EDIT,
  );
  const statusReportsEnabled = useFeatureFlag("status-reports-cutover");
  // Org-gated by the status-reports flag, user-gated by settings.edit.
  const canEditReportSettings =
    hasReportSettingsPermission && statusReportsEnabled;
  const organization = useOrganizationStore((state) => state.organization);
  const agentMap = useAgentWorkflowStore((state) => state.agentMap);
  const fetchAgents = useAgentWorkflowStore((state) => state.fetchAgents);
  const inboxItemMap = useAgentTasksStore((state) => state.itemMap);
  const fetchInboxItems = useAgentTasksStore((state) => state.fetchInboxItems);
  const standardAgentName = useStandardAgentDisplayName();
  const demandForecastingEnabled = useFeatureFlag("demand-forecasting");
  const canViewDemandForecasts = useHasPermission(
    Permission.DEMANDFORECAST_VIEW,
  );
  const emailSearchEnabled = useFeatureFlag("email-search");
  const customRolesEnabled = useFeatureFlag("custom-roles");
  const { pathname, search } = useLocation();
  const navigate = useNavigate();

  // --- Derived values ---
  const canViewUsers = useHasPermission(Permission.USERS_VIEW);
  const canViewOnboarding = useHasPermission(Permission.ONBOARDING_VIEW);
  const canEditOnboardingSettings = useHasPermission(
    Permission.SETTINGS_ONBOARDING_EDIT,
  );
  const onboardingSummary = useOnboardingStore((state) => state.summary);
  const fetchOnboardingSummary = useOnboardingStore(
    (state) => state.ensureSummary,
  );
  const hasUser = useAuthStore((state) => Boolean(state.user));

  useEffect(() => {
    if (hasUser && canViewOnboarding) {
      fetchOnboardingSummary();
    }
  }, [hasUser, canViewOnboarding, fetchOnboardingSummary]);

  const onboardingItem =
    canViewOnboarding && onboardingSummary?.plan
      ? { title: "Onboarding", url: "/onboarding", icon: GraduationCap }
      : undefined;
  const canViewOperations = useHasPermission(Permission.OPERATIONS_VIEW);
  const hasAgentPulseAccess = useHasPermission(Permission.AGENTPULSE_VIEW);
  const inboxCount =
    [...inboxItemMap.values()].filter(
      (item) => item.status === AgentWorkflowRunTaskStatus.PENDING_REVIEW,
    ).length + (onboardingSummary?.myOpenTaskCount ?? 0);
  const standardAgentEnabled = organization?.standardAgentEnabled === true;
  const canViewShopRules = useHasPermission(Permission.SHOPRULES_VIEW);
  const canViewItems = useHasPermission(Permission.ITEMS_VIEW);
  const canViewVault = useHasPermission(Permission.VAULT_VIEW);
  const canViewPeople = useHasAnyPermission(
    Permission.PEOPLE_VIEW_ALL,
    Permission.PEOPLE_VIEW_OWN,
  );

  const canViewLocations = useHasPermission(Permission.LOCATIONS_VIEW);
  const canViewCompanies = useHasAnyPermission(
    Permission.COMPANIES_VIEW_ALL,
    Permission.COMPANIES_VIEW_OWN,
  );
  const canViewQuotes = useHasAnyPermission(
    Permission.QUOTES_VIEW_OWN,
    Permission.QUOTES_VIEW_ALL,
  );
  const canViewPriceLists = useHasPermission(Permission.PRICELISTS_VIEW);
  const canViewDatasets = useHasPermission(Permission.DATASETS_VIEW_ALL);
  const recordsItems = getRecordsItems(
    standardAgentEnabled,
    canViewLocations,
    canViewCompanies,
    canViewShopRules,
    canViewPriceLists,
    canViewDatasets,
    canViewVault,
    canViewItems,
    canViewOperations,
    canViewPeople,
    canViewQuotes,
  );
  const isOnRecordsPage = recordsItems.some((item) =>
    isActiveLink(item.url, pathname, search),
  );
  const isOnSettingsPage = pathname.startsWith("/settings");

  // --- Settings slide animation state ---
  const [showSettings, setShowSettings] = useState(isOnSettingsPage);
  const isAnimatingBack = useRef(false);

  useEffect(() => {
    if (isOnSettingsPage) {
      setShowSettings(true);
    }
  }, [isOnSettingsPage]);

  /**
   * Plays the reverse slide animation before navigating away from settings.
   * @param e - The click event to prevent default navigation
   */
  const handleBackToApp = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isAnimatingBack.current = true;
    setShowSettings(false);
  }, []);

  /**
   * Navigates to home after the slide-back animation completes.
   */
  const handleAnimationComplete = useCallback(() => {
    if (isAnimatingBack.current) {
      isAnimatingBack.current = false;
      navigate("/");
    }
  }, [navigate]);

  // --- Org switcher ---
  const fetchMemberships = useOrganizationMembershipsStore(
    (state) => state.fetchMemberships,
  );

  /**
   * Loads the caller's organizations when the footer menu opens, so single-org
   * users pay no extra request on boot.
   * @param open - Whether the footer dropdown is opening
   */
  const handleOrgMenuOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        fetchMemberships();
      }
    },
    [fetchMemberships],
  );

  // --- Records collapsible state ---
  const [isRecordsOpen, setIsRecordsOpen] = useState(isOnRecordsPage);

  useEffect(() => {
    if (isOnRecordsPage) {
      setIsRecordsOpen(true);
    }
  }, [isOnRecordsPage]);

  // --- Agent & nav derived values ---
  const activeAgentCount = Array.from(agentMap.values()).filter(
    (a) => a.status === "ACTIVE",
  ).length;
  const showAiWorkers =
    canEditSettings && (!standardAgentEnabled || activeAgentCount > 0);
  const settingsGroups = getSettingsGroups(
    showAiWorkers,
    emailSearchEnabled,
    canEditSettings,
    customRolesEnabled,
    canEditReportSettings,
    canEditOnboardingSettings,
  );
  const canViewOpportunities = useHasAnyPermission(
    Permission.OPPORTUNITIES_VIEW_OWN,
    Permission.OPPORTUNITIES_VIEW_ALL,
  );
  const primaryItems = getPrimaryItems(
    demandForecastingEnabled,
    canViewOpportunities,
    canViewDemandForecasts,
    canViewAnalytics,
    hasAgentPulseAccess,
  );

  const primaryAgentWorkflowId = organization?.primaryAgentWorkflowId;
  const primaryAgent = primaryAgentWorkflowId
    ? agentMap.get(primaryAgentWorkflowId)
    : undefined;
  const primaryAgentIsActive = primaryAgent?.status === "ACTIVE";
  const primaryAgentId = primaryAgentIsActive ? primaryAgent?.id : undefined;
  const workflowAgentName = primaryAgentIsActive
    ? primaryAgent?.name
    : undefined;

  const primaryAgentUrl = standardAgentEnabled
    ? "/standard-agent"
    : primaryAgentId
      ? `/agents/${primaryAgentId}`
      : "/settings";
  const primaryAgentLabel = standardAgentEnabled
    ? standardAgentName
    : workflowAgentName || "Set primary agent";
  const PrimaryAgentIcon = standardAgentEnabled
    ? Bot
    : workflowAgentName
      ? getAgentIcon(workflowAgentName)
      : Bot;

  const primaryAgentIsSelected = isActiveLink(
    primaryAgentUrl,
    pathname,
    search,
  );

  // --- Data fetching ---
  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  useEffect(() => {
    fetchInboxItems();
    return () => {};
  }, [fetchInboxItems]);

  return (
    <Sidebar>
      <SidebarHeader className="border-b border-sidebar-border/60">
        <div className="flex items-center px-2 py-3">
          <img
            src={logoBlack}
            alt="Forgepoint"
            className="h-6 w-auto dark:hidden"
          />
          <img
            src={logoWhite}
            alt="Forgepoint"
            className="hidden h-6 w-auto dark:block"
          />
        </div>
      </SidebarHeader>
      <SidebarContent className="scrollbar-transparent relative !overflow-hidden">
        <motion.div
          animate={{ x: showSettings ? "-50%" : "0%" }}
          transition={{ duration: 0.3, ease: [0.32, 0.72, 0, 1] }}
          onAnimationComplete={handleAnimationComplete}
          className="flex h-full w-[200%]"
        >
          <SidebarMainPanel
            onboardingItem={onboardingItem}
            primaryAgentUrl={primaryAgentUrl}
            primaryAgentLabel={primaryAgentLabel}
            PrimaryAgentIcon={PrimaryAgentIcon}
            primaryAgentIsSelected={primaryAgentIsSelected}
            primaryItems={primaryItems}
            inboxCount={inboxCount}
            recordsItems={recordsItems}
            isRecordsOpen={isRecordsOpen}
            onRecordsOpenChange={setIsRecordsOpen}
            pathname={pathname}
            search={search}
          />
          <SidebarSettingsPanel
            settingsGroups={settingsGroups}
            onBackToApp={handleBackToApp}
            pathname={pathname}
            search={search}
          />
        </motion.div>
      </SidebarContent>
      <SidebarFooter className="border-t border-sidebar-border/60">
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu onOpenChange={handleOrgMenuOpenChange}>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton>
                  <Building2 />
                  <span>{organizationName}</span>
                  <ChevronUp className="ml-auto" />
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                side="top"
                className="w-[--radix-popper-anchor-width]"
              >
                <OrgSwitcherMenuItems />
                {canViewUsers && (
                  <DropdownMenuItem asChild>
                    <Link to="/teams">
                      <Users className="mr-2 h-4 w-4" />
                      Users
                    </Link>
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem asChild>
                  <Link to="/settings">
                    <SettingsIcon className="mr-2 h-4 w-4" />
                    Organization Settings
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
